<!--
  @component ServiceRecordList
  @description Registro unificado de transacciones prestacionales realizadas. Solo lectura.
-->
<template>
  <div class="sr-list">
    <div class="sr-list__toolbar">
      <el-input
        v-model="searchInput"
        placeholder="Buscar por TRX, afiliado o prestación..."
        prefix-icon="el-icon-search"
        clearable
        size="small"
        style="width: 280px"
        @input="onSearch"
      />
      <el-select
        v-model="filterOrigin"
        placeholder="Todos los orígenes"
        clearable
        size="small"
        style="width: 170px"
        @change="loadRecords"
      >
        <el-option label="Validación directa" value="validation" />
        <el-option label="Autorización" value="authorization" />
        <el-option
          label="Validación en liquidación"
          value="liquidation_ex_post"
        />
        <el-option
          label="Aceptación manual"
          value="liquidation_manual_override"
        />
      </el-select>
      <el-select
        v-model="filterMonth"
        placeholder="Mes"
        clearable
        size="small"
        style="width: 120px"
        @change="loadRecords"
      >
        <el-option
          v-for="m in months"
          :key="m.value"
          :label="m.label"
          :value="m.value"
        />
      </el-select>
      <el-select
        v-model="filterYear"
        placeholder="Año"
        clearable
        size="small"
        style="width: 90px"
        @change="loadRecords"
      >
        <el-option v-for="y in years" :key="y" :label="y" :value="y" />
      </el-select>
    </div>

    <div class="sr-list__card">
      <el-table
        v-loading="isLoading"
        :data="records"
        :row-class-name="rowClassName"
        size="small"
        border
        style="width: 100%"
        @row-click="onRowClick"
      >
        <el-table-column label="TRX" width="120">
          <template slot-scope="{ row }">
            <span
              class="sr-list__trx"
              :class="{ 'sr-list__trx--tombstone': row._tombstone }"
            >
              {{ row.transactionCode || "-" }}
            </span>
          </template>
        </el-table-column>

        <!-- UC-REG-222 — Fecha más angosta (sin hora, dd/MM/yyyy) -->
        <el-table-column label="Fecha" width="96">
          <template slot-scope="{ row }">
            <span v-if="row._tombstone" class="sr-list__tombstone-text">—</span>
            <span v-else class="sr-list__date">
              {{ row.performedAt | formatDate }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Afiliado" min-width="150">
          <template slot-scope="{ row }">
            <span v-if="row._tombstone" class="sr-list__tombstone-text">
              — transacción anulada durante procesamiento —
            </span>
            <div v-else class="sr-list__member-name">
              {{ memberName(row) }}
            </div>
          </template>
        </el-table-column>

        <!-- UC-REG-199 — Plan como columna aparte -->
        <el-table-column label="Plan" width="110">
          <template slot-scope="{ row }">
            <span v-if="!row._tombstone" class="sr-list__plan">
              {{ row.memberSnapshot.planName || "—" }}
            </span>
          </template>
        </el-table-column>

        <!-- UC-REG-222 — Prestación separada en Código + nombre -->
        <el-table-column label="Código" width="92">
          <template slot-scope="{ row }">
            <span v-if="!row._tombstone" class="sr-list__practice-code">{{
              row.practiceSnapshot.code
            }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Prestación"
          min-width="190"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span v-if="!row._tombstone" class="sr-list__practice-name">{{
              row.practiceSnapshot.name
            }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Categoría" width="110">
          <template slot-scope="{ row }">
            <span
              v-if="!row._tombstone"
              class="sr-list__badge sr-list__badge--category"
            >
              {{ categoryLabel(row.practiceSnapshot.category) }}
            </span>
          </template>
        </el-table-column>

        <!-- UC-REG-222 — Prestador separado: N° en una columna, nombre en
             otra, cada uno en una sola línea. -->
        <el-table-column label="N° Prestador" width="128">
          <template slot-scope="{ row }">
            <span v-if="!row._tombstone" class="sr-list__provider-code">
              {{ providerCode(row) || "—" }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="Prestador"
          min-width="150"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span v-if="!row._tombstone" class="sr-list__provider-name-col">
              {{ providerName(row.providerSnapshot, row.provider) }}
            </span>
          </template>
        </el-table-column>

        <!-- UC-REG-215 — Header "Valor" (antes "Valor aprobado") -->
        <el-table-column label="Valor" width="120" align="right">
          <template slot-scope="{ row }">
            <span v-if="!row._tombstone" class="sr-list__amount">
              {{ formatMoney(approvedAmount(row)) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Origen" width="160">
          <template slot-scope="{ row }">
            <span
              v-if="!row._tombstone"
              class="sr-list__badge"
              :class="originClass(row.origin, row.wasAudited)"
            >
              {{ originLabel(row.origin, row.wasAudited) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Estado" width="140">
          <template slot-scope="{ row }">
            <span
              class="sr-list__liq"
              :class="'sr-list__liq--' + combinedStatus(row).kind"
            >
              {{ combinedStatus(row).label }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <div class="sr-list__pagination">
        <el-pagination
          :current-page="currentPage"
          :page-size="limit"
          :total="total"
          layout="total, prev, pager, next"
          small
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "debounce";
import serviceRecordService from "@/services/service-record";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component ServiceRecordList */
export default {
  name: "ServiceRecordList",

  filters: {
    /**
     * @description Formatea fecha ISO a dd/MM/yyyy HH:mm
     * @param {string} value
     * @returns {string}
     */
    formatDate(value) {
      if (!value) return "-";
      try {
        // UC-REG-215 — solo fecha, sin horario.
        return format(parseISO(value), "dd/MM/yyyy", { locale: es });
      } catch {
        return "-";
      }
    },
  },

  data() {
    return {
      records: [],
      isLoading: false,
      total: 0,
      currentPage: 1,
      limit: 20,
      searchInput: "",
      filterOrigin: "",
      filterMonth: new Date().getMonth() + 1,
      filterYear: new Date().getFullYear(),
      months: [
        { value: 1, label: "Enero" },
        { value: 2, label: "Febrero" },
        { value: 3, label: "Marzo" },
        { value: 4, label: "Abril" },
        { value: 5, label: "Mayo" },
        { value: 6, label: "Junio" },
        { value: 7, label: "Julio" },
        { value: 8, label: "Agosto" },
        { value: 9, label: "Septiembre" },
        { value: 10, label: "Octubre" },
        { value: 11, label: "Noviembre" },
        { value: 12, label: "Diciembre" },
      ],
      years: [2024, 2025, 2026, 2027],
    };
  },

  created() {
    this.onSearch = debounce(this.loadRecords, 350);
    this.loadRecords();
  },

  methods: {
    /** @description Carga transacciones con filtros activos */
    async loadRecords() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.limit,
          limit: this.limit,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterOrigin) params.origin = this.filterOrigin;
        if (this.filterMonth) params.billingMonth = this.filterMonth;
        if (this.filterYear) params.billingYear = this.filterYear;
        // UC-REG-217 — filtro por prestador vía query (click desde el
        //   tab "Por prestador").
        if (this.$route.query.provider) {
          params.providerId = this.$route.query.provider;
        }
        // UC-REG-199 — pedir tombstones para que huecos del counter queden
        //   visibles como fila greyed "anulada durante procesamiento".
        params.includeGaps = "true";
        const result = await serviceRecordService.get(params);
        this.records = result.docs;
        this.total = result.total;
      } catch (e) {
        this.$message.error("Error al cargar las transacciones");
      } finally {
        this.isLoading = false;
      }
    },

    /** @param {number} page */
    onPageChange(page) {
      this.currentPage = page;
      this.loadRecords();
    },

    /**
     * @description UC-REG-199 — Clase por fila. Tombstones se pintan gris.
     * @param {{row:Object}} p
     * @returns {string}
     */
    rowClassName({ row }) {
      return row._tombstone ? "sr-list__row--tombstone" : "";
    },

    /**
     * @description UC-REG-199 — Click navega al detalle de la TRX en su
     *   propia pantalla. Ignora tombstones (no tienen _id real).
     * @param {Object} row
     */
    onRowClick(row) {
      if (!row || row._tombstone || !row._id) return;
      this.$router.push({
        name: "service-record-detail",
        params: { id: row._id },
      });
    },

    /**
     * @description UC-REG-199 — Monto aprobado a mostrar en la grilla.
     *   Prioriza valorization/expectedAmount del SR (post-débitos ya está
     *   reflejado en el propio SR — para MVP usamos expectedAmount).
     * @param {Object} row
     * @returns {number}
     */
    approvedAmount(row) {
      if (!row) return 0;
      return Number(row.expectedAmount || row.valorization?.amount || 0);
    },

    /**
     * @description Formato $XX.XXX con separador es-AR.
     * @param {number} n
     * @returns {string}
     */
    formatMoney(n) {
      if (n == null || n === 0) return "—";
      return `$${Number(n).toLocaleString("es-AR")}`;
    },

    /**
     * @method providerName
     * @method titleCase — UC-REG-224. Capitaliza la inicial de cada palabra
     *   (los nombres se cargan en minúsculas: "ares, federico" → "Ares,
     *   Federico"). Solo presentación, no muta el dato. Respeta acentos.
     * @param {string} str
     * @returns {string}
     */
    titleCase(str) {
      if (!str) return "";
      return String(str)
        .toLowerCase()
        .replace(
          /(^|[\s,.'-])(\p{L})/gu,
          (m, sep, chr) => sep + chr.toUpperCase()
        );
    },

    /**
     * @method memberName — UC-REG-224. Afiliado en Capital Case
     *   ("Apellido, Nombre").
     * @param {Object} row
     * @returns {string}
     */
    memberName(row) {
      const s = row.memberSnapshot || {};
      return `${this.titleCase(s.lastName)}, ${this.titleCase(s.firstName)}`;
    },

    /**
     * @description Resuelve el nombre del prestador desde snapshot o populate
     * @param {Object} snapshot - providerSnapshot del ServiceRecord
     * @param {Object} provider - provider populado del ServiceRecord
     * @returns {string}
     */
    providerName(snapshot, provider) {
      if (snapshot?.firstName)
        return this.titleCase(`${snapshot.lastName}, ${snapshot.firstName}`);
      if (provider?.firstName)
        return this.titleCase(`${provider.lastName}, ${provider.firstName}`);
      return "—";
    },

    /**
     * @method providerCode — UC-REG-215. Número de prestador (PR-NNNNNN).
     *   Viene del snapshot o del provider populado (doctor.providerCode).
     * @param {Object} row
     * @returns {string}
     */
    providerCode(row) {
      const s = row.providerSnapshot;
      if (s && s.providerCode) return s.providerCode;
      const p = row.provider;
      if (p && p.doctor && p.doctor.providerCode) return p.doctor.providerCode;
      return "";
    },

    /**
     * @param {string} origin
     * @param {boolean} wasAudited
     * @returns {string}
     */
    originClass(origin, wasAudited) {
      if (origin === "authorization" && wasAudited)
        return "sr-list__badge--audited";
      if (origin === "authorization") return "sr-list__badge--auth";
      // UC-REG-198 — clases visuales distintas para ex-post y manual
      if (origin === "liquidation_ex_post") return "sr-list__badge--expost";
      if (origin === "liquidation_manual_override")
        return "sr-list__badge--manual";
      return "sr-list__badge--validation";
    },

    /**
     * @param {string} origin
     * @param {boolean} wasAudited
     * @returns {string}
     */
    originLabel(origin, wasAudited) {
      if (origin === "authorization" && wasAudited) return "Auth. auditada";
      if (origin === "authorization") return "Autorización";
      // UC-REG-198 — labels específicos para las TRXs creadas via submission.
      //   Antes caían al default "Validación directa" que era semánticamente
      //   incorrecto (esas TRXs no se validaron en vivo).
      if (origin === "liquidation_ex_post") return "Validación en liquidación";
      if (origin === "liquidation_manual_override") return "Aceptación manual";
      return "Validación directa";
    },

    /**
     * @method combinedStatus — UC-REG-198. Combina SR.status (motor) +
     *   SR.liquidationStatus (financiero) + vínculo con submission en
     *   estado 'reviewing' en un solo chip para la columna Estado.
     *
     *   Orden de prioridad:
     *     1. Rechazada (SR.status rejected / rules_rejected)
     *     2. Anulada (SR creado por submission después anulado)
     *     3. Pagada / Enviada a pagar / Facturada / Liquidada (ciclo cobro)
     *     4. En revisión (vinculado a submission 'reviewing')
     *     5. Sin liquidar (default)
     *
     * @param {Object} row - ServiceRecord doc del listado
     * @returns {{kind: string, label: string}}
     */
    combinedStatus(row) {
      if (row.status === "rejected" || row.status === "rules_rejected") {
        // Distinción visual: si origin es liquidation_ex_post o
        // manual_override y status=rejected → seguramente el submission
        // que lo creó se anuló. Se muestra "Anulada" (chip tachado).
        if (
          row.origin === "liquidation_ex_post" ||
          row.origin === "liquidation_manual_override"
        ) {
          return { kind: "void", label: "Anulada" };
        }
        return { kind: "rejected", label: "Rechazada" };
      }
      const liq = row.liquidationStatus || "pending";
      if (liq === "paid") return { kind: "paid", label: "Pagada" };
      if (liq === "sent_for_payment")
        return { kind: "sent", label: "Enviada a pagar" };
      if (liq === "invoiced") return { kind: "invoiced", label: "Facturada" };
      if (liq === "liquidated")
        return { kind: "liquidated", label: "Liquidada" };
      // TODO(UC-REG-198): consultar si hay submission 'reviewing'
      //   vinculado a este SR para mostrar "En revisión". Requiere
      //   join extra en el endpoint. Por ahora pending = "Sin liquidar".
      return { kind: "pending", label: "Sin liquidar" };
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusClass(status) {
      return (
        {
          // UC-REG-195 — pending_billing = "sin liquidar todavía" (naranja/warn).
          //   billed = "liquidada" en un submission (verde/ok).
          pending_billing: "sr-list__badge--pending",
          billed: "sr-list__badge--billed",
          rules_rejected: "sr-list__badge--rejected",
          rejected: "sr-list__badge--rejected",
        }[status] || ""
      );
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusLabel(status) {
      return (
        {
          // UC-REG-195 — labels más claros: "Sin liquidar" vs "Liquidada".
          //   Antes decía "Lista para facturar" (confuso — sonaba como algo
          //   "listo para pasar al siguiente paso" cuando en realidad NO
          //   había pasado por ningún submission todavía).
          pending_billing: "Sin liquidar",
          billed: "Liquidada",
          rules_rejected: "Rechazada",
          rejected: "Rechazada",
        }[status] || status
      );
    },

    /**
     * @description UC-REG-178 Fase 1 — Label del chip Liquidación.
     * @param {string} status - pending | liquidated | invoiced | paid
     * @returns {string}
     */
    liquidationLabel(status) {
      return (
        {
          pending: "Pendiente",
          liquidated: "Liquidada",
          invoiced: "Facturada",
          sent_for_payment: "Enviada a pagar",
          paid: "Pagada",
        }[status] || "Pendiente"
      );
    },

    /**
     * @description UC-REG-178/183 — Clase CSS del chip Liquidación.
     * @param {string} status
     * @returns {string}
     */
    liquidationClass(status) {
      return (
        {
          pending: "sr-list__liq--pending",
          liquidated: "sr-list__liq--liquidated",
          invoiced: "sr-list__liq--invoiced",
          sent_for_payment: "sr-list__liq--sent",
          paid: "sr-list__liq--paid",
        }[status] || "sr-list__liq--pending"
      );
    },

    /**
     * @param {string} category
     * @returns {string}
     */
    categoryLabel(category) {
      return (
        {
          consultation: "Consulta",
          lab: "Laboratorio",
          imaging: "Imágenes",
          surgery: "Cirugía",
          dental: "Odontología",
          mental_health: "Salud mental",
          rehabilitation: "Rehabilitación",
          emergency: "Urgencias",
          hospitalization: "Internación",
          maternity: "Maternidad",
          medication: "Medicación",
          supply: "Insumos",
          prosthesis: "Prótesis",
          other: "Otros",
        }[category] ||
        category ||
        "-"
      );
    },
  },
};
</script>

<style scoped>
.sr-list {
  font-family: "Plus Jakarta Sans", sans-serif;
  /* UC-REG-215 — matar el cero-tachado (slashed zero) en toda la tabla */
  font-feature-settings: "zero" 0;
}

.sr-list__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.sr-list__card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

/* UC-REG-215 — Sin monospace (el SF Mono del Mac dibuja el 0 tachado que
   Federico odia). Font normal + negro común, como la columna Fecha. */
.sr-list__trx {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

.sr-list__date {
  font-size: 12px;
  color: #6b7294;
  white-space: nowrap;
}

.sr-list__member-name {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

/* UC-REG-215 — Prestador: número arriba (negro), nombre chico debajo */
.sr-list__provider-code {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}
.sr-list__provider-name {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}
/* UC-REG-222 — Nombre del prestador como columna propia (una sola línea). */
.sr-list__provider-name-col {
  font-size: 13px;
  color: #1a1f36;
  white-space: nowrap;
}

.sr-list__member-meta {
  font-size: 11px;
  color: #05e5ba;
  font-weight: 600;
  margin-top: 1px;
}

/* UC-REG-199 — Columna Plan separada del Afiliado */
.sr-list__plan {
  font-size: 12.5px;
  color: #1a1f36;
  font-weight: 500;
}

/* UC-REG-215 — Valor: font normal + negro común (sin monospace verde).
   Solo tabular-nums para que los números queden alineados a la derecha. */
.sr-list__amount {
  font-size: 13px;
  color: #1a1f36;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "zero" 0;
}

/* UC-REG-199 — Tombstones */
.sr-list__trx--tombstone {
  color: #94a3b8 !important;
  font-style: italic;
  font-weight: 500;
}
.sr-list__tombstone-text {
  color: #94a3b8;
  font-style: italic;
  font-size: 12px;
}
::v-deep .sr-list__row--tombstone {
  background: #f8fafc;
  cursor: default;
}
::v-deep .sr-list__row--tombstone td {
  color: #94a3b8;
}

/* Cursor pointer para filas reales (click abre drawer) */
::v-deep .el-table__row {
  cursor: pointer;
}
::v-deep .sr-list__row--tombstone {
  cursor: default;
}

/* UC-REG-215 — Código de prestación en negro común (sin verde llamativo). */
.sr-list__practice-code {
  font-weight: 600;
  color: #1a1f36;
  font-size: 12.5px;
  margin-right: 4px;
}

.sr-list__practice-name {
  color: #1a1f36;
  font-size: 13px;
}

.sr-list__provider {
  font-size: 13px;
  color: #1a1f36;
}

.sr-list__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.sr-list__badge--category {
  background: #eef1f6;
  color: #002d5e;
}

.sr-list__badge--validation {
  background: #e1f5ee;
  color: #085041;
}

.sr-list__badge--auth {
  background: #eeedfe;
  color: #26215c;
}

/* UC-REG-198 — Origen "Validación en liquidación" (creada ex-post por el
   pipeline del submission). Info-azul para distinguirla de la validación
   directa (verde). */
.sr-list__badge--expost {
  background: #dbeafe;
  color: #1e40af;
}
/* UC-REG-198 — Origen "Aceptación manual" (operador aprobó línea que el
   motor había rechazado). Warn-naranja para señalar la intervención. */
.sr-list__badge--manual {
  background: #fef3c7;
  color: #92400e;
}

.sr-list__badge--audited {
  background: #faeeda;
  color: #412402;
}

.sr-list__badge--pending {
  background: #faeeda;
  color: #854f0b;
}

.sr-list__badge--billed {
  background: #eaf3de;
  color: #3b6d11;
}

.sr-list__badge--rejected {
  background: #fcebeb;
  color: #a32d2d;
}

/* UC-REG-178 Fase 1 — chip Liquidación */
.sr-list__liq {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
}
.sr-list__liq--pending {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
}
.sr-list__liq--liquidated {
  background: #ecfdf5;
  color: #065f46;
  border-color: #a7f3d0;
}
.sr-list__liq--invoiced {
  background: #dbeafe;
  color: #1e40af;
  border-color: #93c5fd;
}
.sr-list__liq--sent {
  background: #ede9fe;
  color: #5b21b6;
  border-color: #c4b5fd;
}
.sr-list__liq--paid {
  background: #d1fae5;
  color: #065f46;
  border-color: #6ee7b7;
}
/* UC-REG-198 — reglas nuevas del chip combinado */
.sr-list__liq--rejected {
  background: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}
.sr-list__liq--void {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}

.sr-list__pagination {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f2f7;
}
</style>
